import React, {FC, useState, useEffect} from "react";
import { useTask } from '../stateManagement/context';
import {
    AdequacyTypeID,
    ComponentTypeID,
    IControlType, IHarm,
    IProjectFailureMode, ISeverity, ISummaryKnowledge
} from "../types/types";

import {
    Chip,
    Collapse, IconButton,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip
} from "@material-ui/core";
import SeveritySelection from "./severity_selection";
import {WarningChip} from "./reasonably_practicable_tests";
import Comments from "./comments";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import NumberFormat from 'react-number-format';


interface NumberFormatCustomProps {
    inputRef: (instance: NumberFormat<number> | null) => void;
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}


function NumberFormatDollar(props: NumberFormatCustomProps) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            prefix="$"
        />
    );
}

function NumberFormatCustom(props: NumberFormatCustomProps) {
    const {inputRef, onChange, ...other} = props;

    return (
        <NumberFormat
            {...other}
            getInputRef={inputRef}
            onValueChange={(values) => {
                onChange({
                    target: {
                        name: props.name,
                        value: values.value,
                    },
                });
            }}
            thousandSeparator
            isNumericString
            prefix=""
        />
    );
}

interface IHarmProps {
    severities: ISeverity[]
    failure_mode: IProjectFailureMode
    harm: IHarm
    updateHarm: (harm: IHarm) => void;
}


export const Consequence = (h: IHarm, name?:string) => {
    if (!h.consequence)
        return WarningChip(name?name:"undefined")

    let level = h.consequence.level

    let color = "#535353"
    let bcolor = "rgb(196,196,196)";
    let alpha = 1

    if (level > 0) {
        bcolor = "rgba(206,238,208," + alpha + ")";
        color = "rgb(48,104,33)";
    }
    if (level > 3) {
        bcolor = "rgba(252,236,166," + alpha + ")";
        color = "rgb(152,96,34)";
    }
    if (level > 6) {
        bcolor = "rgba(251,84,81," + alpha + ")";
        color = "rgb(104,4,0)";
    }

    return (
        <div>
            <Tooltip title={h.consequence.name}>
            <Chip
                size="small"
                label={name? name : h.consequence.name}
                color="primary"
                style={{backgroundColor: bcolor, color: color}}
            />
            </Tooltip>
        </div>
        )
}

const Harm: FC<IHarmProps> = ({
                                  severities,
                                  failure_mode,
                                  harm,
                                  updateHarm
                              }) => {

    const [newHarm, setNewHarm]=
        useState<IHarm>(harm);

    const [open, setOpen] = useState(false);
    let tasks = useTask()
    
    const [write_access, setWriteAccess] = useState<boolean>(false);

    useEffect(() => {
        setWriteAccess(tasks.write_access)
    }, [tasks])

    const graph_data = [
        ["Very Low", "Low", "Significant", "High C"],
        ["Significant", "Significant", "High C", "High B"],
        ["", "High C", "High B", "High A"],
        ["", "High A", "High A", "Extreme"],
        ["", "", "Extreme", "Extreme"]]

    const ppl_levels = ["< 0.1","0.1 - 1","1 - 5","5 - 50",">50"]


    const getSeverityID = () => {
        if (harm.severity_id) {
            return harm.severity_id.toString()
        }
        return ""
    }

    const renderField = <T,>(obj: T, key: keyof T,
        label: string,
        type: string,
        setObj: (obj: T) => void,
        updateObj: (obj: T) => void,
        write_access: boolean) => { // Added write_access parameter

        const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
            let v = event.target.value;
            let c = { ...obj };

            if (type === "dollar") {
                let numberValue = Number(v.replace(/[^0-9\.-]+/g, ""));
                (c as any)[key] = numberValue;
            } else if (type === "number") {
                (c as any)[key] = parseFloat(v);
            } else { // type === "string"
                (c as any)[key] = v;
            }

            setObj(c);
        };

        return (
            <div>
                <div style={{
                    width: "100%",
                    margin: "auto",
                }}>
                    <TextField
                        fullWidth
                        style={{ width: '100%' }}
                        inputProps={{ style: { fontSize: "11pt" } }}
                        defaultValue={obj[key]}
                        disabled={!write_access}
                        multiline={true}
                        onChange={handleOnChange}
                        onBlur={() => updateObj({ ...obj })}
                        type={type !== "dollar" ? type : undefined}
                        InputProps={type === "dollar" ? {
                            style: { fontSize: 14, textAlign: "right" },
                            inputComponent: NumberFormatDollar as any,
                        } : undefined}
                    />
                </div>
            </div>
        );
    };


    return (
        <>
        <TableRow>
            <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
        </TableCell>
            <TableCell>{failure_mode.failure_mode?.name}</TableCell>

            <TableCell><SeveritySelection severities={severities}
                                          initial_selection_id={getSeverityID()}
                                          harm={harm}
                                          updateHarm={updateHarm}/>
            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "population_at_risk",
                    "PAR",
                    "number",
                    setNewHarm,
                    updateHarm,
                    write_access)}


            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "potential_life_loss",
                    "PLL",
                    "number",
                    setNewHarm,
                    updateHarm,
                    write_access)}
            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "economics",
                    "economics",
                    "dollar",
                    setNewHarm,
                    updateHarm,
                    write_access)}
            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "environmental",
                    "environmental/other",
                    "dollar",
                    setNewHarm,
                    updateHarm,
                    write_access)}
            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "special_consideration",
                    "special consideration",
                    "string",
                    setNewHarm,
                    updateHarm,
                    write_access)}
            </TableCell>
            <TableCell>
                {renderField<IHarm>(
                    newHarm,
                    "special_consideration_description",
                    "special consideration description",
                    "string",
                    setNewHarm,
                    updateHarm,
                    write_access)}
            </TableCell>
            <TableCell>{Consequence(harm)}</TableCell>
            <TableCell>{failure_mode.id && <Comments parent_uuid={failure_mode.id} type_id={ComponentTypeID.Harm}/>}</TableCell>


        </TableRow>


    <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }}colSpan={1}/>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={open} timeout="auto" unmountOnExit>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell></TableCell>
                            <TableCell align={"center"} colSpan={4}><b>Severity of Damage and Loss</b></TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>PLL</TableCell>
                            {severities.map(
                                (ad, index) => {
                                    return (
                                        <TableCell> {ad.name} </TableCell>
                                    ) }
                            )}
                        </TableRow>


                    </TableHead>

                    <TableBody>
                        {ppl_levels.map(
                            (ad, index) => {
                                return (
                                    <TableRow>
                                        <TableCell> {ad} </TableCell>

                                        {graph_data[index].map( (e, index_i) => {
                                            let found = false
                                            let found1 = false

                                            let life_thresholds = [
                                                0, 0.1, 1, 5, 50
                                            ]

                                            let row_index = -1

                                            if (harm.potential_life_loss == 0)
                                                row_index = 0
                                            let ppl =  harm.potential_life_loss ?  harm.potential_life_loss : 0
                                            for (let i = life_thresholds.length-1; i > -1; i--) {
                                                if (life_thresholds[i] < ppl) {
                                                    row_index = i
                                                    break
                                                }
                                            }

                                            if (row_index == index) {
                                                found = true
                                            }
                                            let ex = severities[index_i]
                                            if (ex.id === harm.severity_id) {
                                                found1 = true
                                            }

                                            let style = {}
                                            if (found && found1) {
                                                style = ({ ...style, fontWeight:  "bold",
                                                    outline: "4px solid black",
                                                    outlineOffset: "-4px"})
                                            }

                                            if (e === "") {
                                                style = ({ ...style,
                                                    backgroundColor:  "rgb(196,196,196)",
                                                    color: "#535353"
                                                })
                                            }


                                            if (e === "Very Low" || e === "Low"|| e === "Significant") {
                                                style = ({ ...style,
                                                    backgroundColor:  "rgb(206,238,208)",
                                                    color: "rgb(48,104,33)"
                                                })
                                            }
                                            if (e === "High A" || e === "High B"|| e === "High C") {
                                                style = ({ ...style,
                                                    backgroundColor:  "rgb(252,236,166)",
                                                    color: "rgb(152,96,34)"
                                                })
                                            }
                                            if (e === "Extreme") {
                                                style = ({ ...style,
                                                    backgroundColor:  "rgb(251,84,81)",
                                                    color: "rgb(104,4,0)"
                                                })
                                            }

                                            return <TableCell style={style}>

                                                {e}
                                            </TableCell>
                                        })}
                                    </TableRow>
                                )}
                        )}
                    </TableBody>

                </Table>

            </Collapse>
        </TableCell>
    </TableRow>
    </>
    );
}

export default Harm


